import React, { useState, useEffect, useRef, useCallback } from 'react';
import Modal from 'react-modal';
import APIUtils from '../../../utils/APIUtils';
import SessionConstants from '../../../config/SessionConstants';
import SuccessMessageModal from '../../SuccessMessageModal';
import { InputLabel, MenuItem, Select, FormControl } from '@mui/material';
import { AddCircleOutline, Error } from "@mui/icons-material";
import Loading from '../../Loading'
import InterpreterAutocomplete from '../../../uiComponents/MuiAutocomplete/InterpreterAutocomplete';
import { format } from 'date-fns';
import { DateUtils } from '../../../utils/DateUtils';
import './index.css'

const ASSIGNED_INTERPRETERS_ERROR = "Assigned interpreters exceeds the number of required interpretations";
const DUPLICATE_INTERPRETERS_ERROR = "Please remove duplicate interpreters";

const VariantSettingsModal = (props) => {
  const [variantReviewId, setVariantReviewId] = useState(props.reviewData.variantReviewId);
  const [dueDate] = useState(DateUtils.FormatLocalDate(props.reviewData.dueDate));
  const [reminderFrequency, setReminderFrequency] = useState(props.reviewData.reminderFrequency);
  const [initReqInterpreters, setInitReqInterpreters] = useState(props.reviewData.requiredInterpretations);
  const [requiredInterpretations, setRequiredInterpretations] = useState(props.reviewData.requiredInterpretations);
  const [initVariantInterpretations, setInitVariantInterpretations] = useState([]);
  const [variantInterpretations, setVariantInterpretations] = useState(props.interpretationData);
  const [initInterpreters, setInitInterpreters] = useState([]);
  const [defaultInterpreters, setDefaultInterpreters] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [minInterpreters, setMinInterpreters] = useState(1);
  const [errorMessage, setErrorMessage] = useState([]);
  const [successMessageModalIsOpen, setSuccessMessageModalIsOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userValues, setUserValues] = useState([]);
  const [hasSortedUserValues, setHasSortedUserValues] = useState(false);
  const [comboboxErrors, setComboboxErrors] = useState({});

  const isMounted = useRef(false);

  // Fetch users and set up state on mount
  useEffect(() => {
    isMounted.current = true;
    const fetchData = async () => {
      try {
        const programId = sessionStorage.getItem(SessionConstants.PROGRAM_ID);
        const users = await APIUtils.getProgramUsers(programId);

        let defaultInterpretersArr = [];
        let interpretationCompletedCount = 0;

        for (let i = 0; i < variantInterpretations.length; i++) {
          defaultInterpretersArr.push(variantInterpretations[i].user.email);
          if (variantInterpretations[i].interpretationStatus === 'Completed') {
            interpretationCompletedCount++;
          }
        }

        if (isMounted.current) {
          setAvailableUsers(users);
          setDefaultInterpreters(defaultInterpretersArr);
          setInitInterpreters([...defaultInterpretersArr]);
          setInitVariantInterpretations([...variantInterpretations]);
          setRequiredInterpretations(requiredInterpretations);
          setInitReqInterpreters(requiredInterpretations);
          setMinInterpreters(interpretationCompletedCount);
          setLoading(false);
          initializeDefaultUsers(users, variantInterpretations);

          // Signal parent that modal is ready
          if (props.onReady) props.onReady();
        }
      } catch (error) {
        console.error("Error loading data:", error);
      }
    };
    fetchData();
    return () => { isMounted.current = false; };
    // eslint-disable-next-line
  }, []);

  // Sort user values and interpreters after mount
  useEffect(() => {
    if (!hasSortedUserValues && userValues.length > 0) {
      // Sort variantInterpretations alphabetically by user.fullName
      const sortedVariantInterpretations = [...variantInterpretations].sort((a, b) =>
        a.user.fullName.localeCompare(b.user.fullName)
      );
      // Sort the userValues and defaultInterpreters from the sorted variantInterpretations
      const sortedUserValues = sortedVariantInterpretations.map((interp) => interp.user.fullName);
      const sortedDefaultInterpreters = sortedVariantInterpretations.map((interp) => interp.user.email);

      setVariantInterpretations(sortedVariantInterpretations);
      setUserValues(sortedUserValues);
      setDefaultInterpreters(sortedDefaultInterpreters);
      setHasSortedUserValues(true);
    }
    // If reviewData changes, stop loading
    // eslint-disable-next-line
  }, [userValues, hasSortedUserValues, variantInterpretations]);

  useEffect(() => {
    if (props.reviewData) setLoading(false);
  }, [props.reviewData]);

  // Initialize userValues with default users
  const initializeDefaultUsers = useCallback((users, variantInterpretationsArr) => {
    const userVals = [];
    if (variantInterpretationsArr.length === 0) {
      userVals.push("");
    } else {
      variantInterpretationsArr.forEach((interpretation, i) => {
        const user = getDefaultUsers(i, users, variantInterpretationsArr);
        if (user) userVals.push(user);
      });
    }
    setUserValues(userVals);
  }, []);

  // Get default user fullName by index
  const getDefaultUsers = (i, users = availableUsers, variantInterpretationsArr = variantInterpretations) => {
    try {
      const user = users.find(
        user => user.email === variantInterpretationsArr[i].user.email
      );
      return user ? user.fullName : "";
    } catch {
      return "";
    }
  };

  // Reset state on close
  const onClose = () => {
    setErrorMessage([]);
    setRequiredInterpretations(initReqInterpreters);
    setDefaultInterpreters([...initInterpreters]);
    setVariantInterpretations([...initVariantInterpretations]);
    setUserValues(initVariantInterpretations.map((interp) => interp.user.fullName));
    setHasSortedUserValues(false);
    props.closeModal();
  };

  // Handle combobox change
  const handleComboboxChange = (index, selectedUser) => {
    setInterpreter(index, selectedUser);
  };

  // Handle input change for autocomplete
  const handleInputChange = (index, inputValue) => {
    const userVals = [...userValues];
    userVals[index] = inputValue;
    setUserValues(userVals);
  };

  // Set interpreter and check for duplicates
  const setInterpreter = (index, selectedUser) => {
    let errorMsg = [];
    let visibleUser = [];
    let defaultInterpretersArr = [...defaultInterpreters];
    let userVals = [...userValues];

    defaultInterpretersArr[index] = selectedUser.email;
    userVals[index] = selectedUser.fullName;

    for (let i = 0; i < defaultInterpretersArr.length; i++) {
      if (defaultInterpretersArr[i]) visibleUser.push(defaultInterpretersArr[i]);
    }

    const uniqueUsers = [...new Set(visibleUser)];
    if (visibleUser.length !== uniqueUsers.length) {
      errorMsg.push(DUPLICATE_INTERPRETERS_ERROR);
    }

    setErrorMessage(errorMsg);
    setDefaultInterpreters(defaultInterpretersArr);
    setUserValues(userVals);
  };

  // Set required interpretations
  const handleRequiredInterpretations = (e) => {
    const required = parseInt(e.target.value);
    if (required >= minInterpreters) {
      setRequiredInterpretations(required);
    }
    if (defaultInterpreters.length <= required) {
      setErrorMessage((prev) =>
        prev.filter((error) => error !== ASSIGNED_INTERPRETERS_ERROR)
      );
    }
  };

  // Add interpreter
  const handleAddInterpreter = () => {
    setDefaultInterpreters((prev) => [...prev, ""]);
    setUserValues((prev) => [...prev, ""]);
  };

  // Delete interpreter
  const handleDeleteInterpreter = (index) => {
    const updatedDefaultInterpreters = defaultInterpreters.filter((_, i) => i !== index);
    const updatedUserValues = userValues.filter((_, i) => i !== index);
    const updatedVariantInterpretations = variantInterpretations.filter((_, i) => i !== index);

    const uniqueUsers = [...new Set(updatedUserValues)];
    const isDuplicatePresent = updatedUserValues.length !== uniqueUsers.length;

    let updatedErrorMessage = errorMessage.filter(
      (error) => error !== ASSIGNED_INTERPRETERS_ERROR
    );
    if (!isDuplicatePresent) {
      updatedErrorMessage = updatedErrorMessage.filter(
        (error) => error !== DUPLICATE_INTERPRETERS_ERROR
      );
    }

    // Remove the error message for the deleted combobox
    const updatedComboboxErrors = { ...comboboxErrors };
    delete updatedComboboxErrors[index];
    // Shift error keys down for any errors after the removed index
    const shiftedComboboxErrors = {};
    Object.keys(updatedComboboxErrors).forEach(key => {
      const keyNum = parseInt(key, 10);
      if (keyNum > index) {
        shiftedComboboxErrors[keyNum - 1] = updatedComboboxErrors[key];
      } else if (keyNum < index) {
        shiftedComboboxErrors[keyNum] = updatedComboboxErrors[key];
      }
    });

    setDefaultInterpreters(updatedDefaultInterpreters);
    setUserValues(updatedUserValues);
    setVariantInterpretations(updatedVariantInterpretations);
    setErrorMessage(updatedErrorMessage);
    setComboboxErrors(shiftedComboboxErrors);
  };

  // Submit handler
  const onSubmit = async () => {
    let errorMsg = [];
    let newComboboxErrors = {};

    // Validate each combobox
    userValues.forEach((userValue, index) => {
      // Only validate editable comboboxes (new ones)
      const isSavedInterpreter = !!variantInterpretations[index];
      if (!isSavedInterpreter) {
        // Must match a user in availableUsers
        const found = availableUsers.some(user => user.fullName === userValue);
        if (!userValue || !found) {
          newComboboxErrors[index] = "Please select an interpreter";
        }
      }
    });

    setComboboxErrors(newComboboxErrors);

    // If any errors, prevent submit
    if (Object.keys(newComboboxErrors).length > 0) return;

    // Filter out empty or invalid interpreters and their user values
    const filteredInterpreters = defaultInterpreters.filter((email, index) => {
      const userValue = userValues[index];
      const found = availableUsers.some(user => user.fullName === userValue);
      return email && email.trim() !== '' && found;
    });
    const filteredUserValues = userValues.filter((user, index) => {
      const found = availableUsers.some(userObj => userObj.fullName === user);
      return user && filteredInterpreters[index] && found;
    });

    if (filteredInterpreters.length > requiredInterpretations) {
      errorMsg.push(ASSIGNED_INTERPRETERS_ERROR);
    }
    const uniqueUsers = [...new Set(filteredUserValues)];
    if (filteredUserValues.length !== uniqueUsers.length) {
      errorMsg.push(DUPLICATE_INTERPRETERS_ERROR);
    }
    setErrorMessage(errorMsg);

    if (errorMsg.length > 0) return;

    let formData = new FormData();
    formData.append("reminderFrequency", reminderFrequency);
    formData.append("requiredInterpretations", requiredInterpretations);
    formData.append("dueDate", format(new Date(dueDate), 'yyyy-MM-dd'));

    await saveVariantSetting(formData, filteredInterpreters, filteredUserValues);
    setInitReqInterpreters(requiredInterpretations);
  };

  // Save variant setting
  const saveVariantSetting = async (formData, filteredInterpreters, filteredUserValues) => {
    setSuccessMessage([]);
    setLoading(true);
    try {
      const response1 = await APIUtils.updateReviewSettings(variantReviewId, formData);
      addSuccessMessage(response1);
      await assignInterpretation(filteredInterpreters);
      await unassignInterpretation(filteredInterpreters);
      setErrorMessage([]);
      setSuccessMessageModalIsOpen(true);
      setLoading(false);
      onClose();
    } catch (err) {
      setErrorMessage([err.toString()]);
      setLoading(false);
    }
  };

  // Assign interpretation
  const assignInterpretation = async (filteredInterpreters) => {
    await Promise.all(filteredInterpreters.map(async (email) => {
      const variantInterpretation = initVariantInterpretations.filter(item => item.user.email === email);
      if (variantInterpretation.length === 0) {
        let formData = new FormData();
        formData.append('userId', email);
        let response = await APIUtils.assignInterpretation(variantReviewId, formData);
        addSuccessMessage(response);
      }
    }));
  };

  // Unassign interpretation
  const unassignInterpretation = async (filteredInterpreters) => {
    await Promise.all(initVariantInterpretations.map(async (variantInterpretation) => {
      const email = filteredInterpreters.filter(email => email === variantInterpretation.user.email);
      if (email.length === 0) {
        let formData = new FormData();
        formData.append('userId', variantInterpretation.user.email);
        let response = await APIUtils.unassignInterpretation(variantReviewId, formData);
        addSuccessMessage(response);
      }
    }));
  };

  // Add success message
  const addSuccessMessage = (message) => {
    setSuccessMessage((prev) => [...prev, message]);
  };

  // Close success modal
  const closeSuccessModal = () => {
    setSuccessMessageModalIsOpen(false);
    props.refresh();
  };

  // Render comboboxes
  const renderComboboxes = () => {
    const completedComboboxes = [];
    const interpreterComboboxes = [];
    const newComboboxes = [];

    for (let i = 0; i < userValues.length; i++) {
      const defaultUser = userValues[i];
      const index = i;
      const isSavedInterpreter = !!variantInterpretations[index];
      const interpretationStatus = variantInterpretations[index]?.interpretationStatus;
      const removeDisabled = interpretationStatus === "Completed";
      const comboboxDisabled = isSavedInterpreter;
      let labelText = "Interpreter (New)";
      if (comboboxDisabled) {
        labelText = removeDisabled
          ? "Interpreter (Interpretation Completed)"
          : "Interpreter (Currently Assigned)";
      }

      const combobox = (
        <InterpreterAutocomplete
          key={i}
          index={index}
          value={availableUsers.find(user => user.fullName === userValues[i]) || null}
          inputValue={userValues[i] || ""}
          options={availableUsers}
          disabled={comboboxDisabled}
          label={labelText}
          onSelectionChange={handleComboboxChange}
          onInputChange={handleInputChange}
          onRemove={handleDeleteInterpreter}
          error={comboboxErrors[i]}
          removeDisabled={removeDisabled}
          showCompletedIcon={removeDisabled}
          ariaLabel={`interpreter_${i + 1}_input`}
        />
      );

      if (labelText === "Interpreter (Interpretation Completed)") {
        completedComboboxes.push({ combobox, name: defaultUser });
      } else if (labelText === "Interpreter") {
        interpreterComboboxes.push({ combobox, name: defaultUser });
      } else {
        newComboboxes.push(combobox);
      }
    }

    // Sort completed and interpreter comboboxes alphabetically by name
    completedComboboxes.sort((a, b) => a.name.localeCompare(b.name));
    interpreterComboboxes.sort((a, b) => a.name.localeCompare(b.name));

    return (
      <>
        {completedComboboxes.map(item => item.combobox)}
        {interpreterComboboxes.map(item => item.combobox)}
        {newComboboxes}
      </>
    );
  };

  return (
    <>
      <Loading isLoading={loading} />
      <div key={props.childKey}>
        <Modal
          className="modal variantSettingsModal"
          overlayClassName="modalOverlay"
          aria-modal="true"
          contentLabel="Variant Review Settings"
          shouldCloseOnOverlayClick={false}
          isOpen={props.modalIsOpen}
          onRequestClose={onClose}
        >
          <div className="modalHeader">
            Variant Review Settings: {props.reviewData.variantName}
          </div>
          <div className="modalContent center">
            <div className="vsm-required-section">
              <div className="sectionHeading center">
                <InputLabel
                  id="numberOfInterpreters-label"
                  htmlFor="numberOfInterpreters"
                  style={{ position: 'static', transform: 'none', color: 'inherit', fontSize: 'inherit' }}
                  shrink
                >
                  Number of Required Interpretations
                </InputLabel>
              </div>
              <div className="subSection flexJustifyCenter requiredInterpSection">
                <div className="modalInput">
                  <FormControl
                    variant="outlined"
                    size="small"
                  >
                    <Select
                      labelId="numberOfInterpreters-label"
                      id="numberOfInterpreters"
                      name="numberOfInterpreters"
                      value={requiredInterpretations || ""}
                      onChange={handleRequiredInterpretations}
                      MenuProps={{
                        disableScrollLock: true,
                        disablePortal: true,
                        anchorOrigin: { vertical: "bottom", horizontal: "left" },
                        transformOrigin: { vertical: "top", horizontal: "left" },
                      }}
                      inputProps={{
                        'aria-labelledby': 'numberOfInterpreters-label',
                      }}
                    >
                      {[1, 2, 3, 4, 5].map(value => (
                        <MenuItem key={value} value={value}>
                          {value}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </div>
            </div>
            <div className="sectionHeading center">
              Assigned Interpreters
            </div>
            <div className="vsm-error-container">
              {errorMessage.length > 0 && (
                <ul className="error" style={{ padding: "0" }}>
                  {errorMessage.map((message, index) => (
                    <li key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem' }}>
                      <span style={{ display: 'flex', alignItems: 'center', marginRight: '8px' }}>
                        <Error fontSize='medium' aria-hidden="true" />
                      </span>
                      <span>
                        {message}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="subSection vsm-interpreters-section">
              {renderComboboxes()}
              {userValues.length < 5 && (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <button
                    onClick={handleAddInterpreter}
                    className="addRemoveButton"
                  >
                    <span className="addRemoveButtonSpan">
                      <AddCircleOutline className="addRemoveIcons" alt="add" fontSize='small' />
                    </span>
                    <span>
                      Add
                    </span>
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="modalButtons">
            <button
              className="modalButton primaryButton"
              onClick={onSubmit}
              disabled={loading}
            >
              Save
            </button>
            <button className="modalButton" onClick={onClose}>
              Cancel
            </button>
          </div>
        </Modal>
        <SuccessMessageModal
          closeModal={closeSuccessModal}
          modalIsOpen={successMessageModalIsOpen}
          message={successMessage}
        />
      </div>
    </>
  );
};

export default VariantSettingsModal;
